import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { getEditor } from './registry'
import { EMPTY_FIELD_VALUE, hasFieldValue } from './fieldValue'
import { largeurUtile } from '../../shell/cornerDock';

const MARGIN = 8
const GAP = 6
// An anchor taller than this share of the viewport (a whole section, a hero)
// is not a useful anchor: the popover then opens at the click point instead.
const TALL_ANCHOR_RATIO = 0.6

const WIDE_TYPES = new Set(['object-ref', 'background'])
// Une collection montre un formulaire complet — ou un tableau d'une ligne par
// élément : 42 rem l'étranglaient (barre de défilement horizontale dès la
// troisième colonne). Elle a sa propre largeur.
const XWIDE_TYPES = new Set(['list', 'stats-list'])
const widthOf = (type) => {
  if (XWIDE_TYPES.has(type)) return 'xwide'
  return WIDE_TYPES.has(type) ? 'wide' : 'narrow'
}

/**
 * Editor surface for every field type that isn't edited in place (link,
 * icon, image, select, background…). It opens ANCHORED to the clicked field —
 * below it, flipped above when there's no room, clamped to the viewport —
 * instead of a centered modal with a backdrop, so the editor stays in the
 * context of what it edits (Page Builder rule: inline everywhere, no
 * modification pop-in).
 *
 * Portal to <body>: a grid cell is positioned by a CSS transform
 * (react-grid-layout) which makes it the containing block for
 * `position: fixed`, and it clips with `overflow: hidden` — rendering at the
 * document root escapes both.
 *
 * Closes on Escape, on the × button, on the editor's own Cancel/Apply, and on
 * a click on another part of the canvas (a click inside a sub-dialog the
 * editor opens elsewhere in <body> — e.g. a list cell's media picker — does
 * NOT close it).
 */
export const FieldEditorPopover = ({ anchorEl, anchorPoint, field, value, companion, onSave, onClose }) => {

  const popoverRef = useRef(null)
  const [position, setPosition] = useState(null)

  // Field.editorType is the legacy name; field.type is the new uniform name.
  // The registry accepts both — they're registered as the same plugin.
  const typeKey = field.type ?? field.editorType
  const plugin = getEditor(typeKey)

  const place = () => {
    const pop = popoverRef.current
    if (!pop) return
    const vw = largeurUtile()
    const vh = window.innerHeight
    const rect = anchorEl?.isConnected ? anchorEl.getBoundingClientRect() : null
    const useRect = rect != null && rect.height < vh * TALL_ANCHOR_RATIO
    const refTop = useRect ? rect.top : (anchorPoint?.y ?? vh / 2)
    const refBottom = useRect ? rect.bottom : (anchorPoint?.y ?? vh / 2)
    const refLeft = useRect ? rect.left : (anchorPoint?.x ?? vw / 2)

    const { width, height } = pop.getBoundingClientRect()
    let top = refBottom + GAP
    if (top + height > vh - MARGIN) {
      const above = refTop - GAP - height
      top = above >= MARGIN ? above : Math.max(MARGIN, vh - MARGIN - height)
    }
    const left = Math.min(Math.max(MARGIN, refLeft), Math.max(MARGIN, vw - MARGIN - width))
    setPosition({ top, left })
  }

  useLayoutEffect(place, [anchorEl, anchorPoint, typeKey])

  useEffect(() => {
    const onWindowChange = () => place()
    window.addEventListener('resize', onWindowChange)
    window.addEventListener('scroll', onWindowChange, true)
    // Un éditeur peut CHANGER DE TAILLE sans que rien ne bouge autour : la
    // collection passe d'un formulaire à un tableau, une liste gagne une
    // ligne, un aperçu d'image se charge. Sans ce suivi, le placement reste
    // celui de la hauteur d'origine et le pied de la pop-in — donc le bouton
    // « Enregistrer » — sort sous le bord de la fenêtre.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onWindowChange)
    if (popoverRef.current) observer?.observe(popoverRef.current)
    return () => {
      window.removeEventListener('resize', onWindowChange)
      window.removeEventListener('scroll', onWindowChange, true)
      observer?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, anchorPoint])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      // Escape inside a sub-dialog the editor opened (media library…) belongs
      // to that dialog, not to us.
      const dialog = e.target?.closest?.('[role="dialog"]')
      if (dialog && dialog !== popoverRef.current) return
      e.stopPropagation()
      onClose()
    }
    const onPointerDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return
      // Une couche flottante ouverte PAR l'éditeur (le menu des champs d'un
      // TokenTextField) vit sur <body>, hors de notre sous-arbre : un clic
      // dedans est un clic DANS l'éditeur. Sans cette réserve, choisir un champ
      // à la souris refermait la pop-in — nous écoutons en CAPTURE, donc le
      // stopPropagation de la couche arrive trop tard.
      if (e.target.closest?.('[data-editor-layer]')) return
      // A click inside a sub-dialog the editor portaled elsewhere in <body>
      // (media library…) belongs to that dialog, not to us.
      const dialog = e.target.closest?.('[role="dialog"]')
      if (dialog && dialog !== popoverRef.current) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('mousedown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('mousedown', onPointerDown, true)
    }
  }, [onClose])

  // Focus the editor on open, give focus back to the field on close.
  useEffect(() => {
    const pop = popoverRef.current
    const focusable = pop?.querySelector('input, textarea, select, [contenteditable="true"], button:not([aria-label="Fermer l\'éditeur"])')
    focusable?.focus?.()
    return () => { anchorEl?.focus?.() }
  }, [anchorEl])

  // Un type s'édite SOIT par une pop-in propre (`Editor`), SOIT par un
  // contrôle posé dans un formulaire (`mode: 'inline'` + `Cell`) — le registre
  // accepte les deux, et promet que le même greffon sert les trois surfaces.
  // N'accepter ici que `Editor` trahissait cette promesse : un champ `term-ref`
  // (une catégorie) cliqué DANS LE RENDU n'ouvrait rien du tout, en silence.
  const inlineOnly = !plugin?.Editor && plugin?.mode === 'inline' && Boolean(plugin?.Cell)
  if (!plugin || (!plugin.Editor && !inlineOnly)) {
    console.warn(`[builder] No editor registered for type "${typeKey}"`)
    return null
  }
  const { Editor, Cell } = plugin
  // Strip `key` from the spread to avoid React's "key in spread" warning —
  // the field object's `key` is data, not a React key.
  const { key: _fieldKey, ...editorConfig } = field

  // Un éditeur `surface: 'fullscreen'` (la médiathèque) porte son propre
  // dialogue en portail : l'habiller d'un popover ancré donnerait deux
  // dialogues imbriqués et une pop-in plein écran clouée sous un cadre de
  // 42 rem. On le rend nu — la fermeture et le clavier sont à lui.
  if (plugin.surface === 'fullscreen') {
    return (
      <Editor
        value={value}
        label={field.label}
        {...editorConfig}
        onSave={(v) => onSave(field.key, v)}
        onClose={onClose}
      />
    )
  }
  const title = `Modifier — ${field.label ?? field.key}`

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={title}
      className="builder-field-popover"
      data-width={widthOf(typeKey)}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="builder-field-popover-head">
        <span className="builder-field-popover-title">{title}</span>
        {/* Un éditeur sait CHOISIR une valeur, aucun ne sait la retirer : sans
            cette commande, une adresse, une icône ou un fond posés une fois ne
            se reprennent plus. Elle vit dans le chrome de la pop-in, donc elle
            existe pour TOUS les types ancrés d'un coup. Pas de confirmation :
            reposer la valeur est un clic. */}
        {!field.required && hasFieldValue(value) && (
          <button
            type="button"
            onClick={() => onSave(field.key, EMPTY_FIELD_VALUE)}
            className="builder-field-popover-clear"
          >
            Vider
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="builder-field-popover-close"
          aria-label="Fermer l'éditeur"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>
      <div className="builder-field-popover-body">
        {inlineOnly
          ? (
            // Un contrôle de formulaire n'a ni « Appliquer » ni « Annuler » :
            // il REND une valeur (une catégorie choisie ou créée). C'est donc
            // son `onChange` qui commite — et `onSave` referme la pop-in, la
            // valeur étant posée.
            <Cell
              value={value}
              placeholder={field.placeholder ?? ''}
              {...editorConfig}
              onChange={(v) => onSave(field.key, v)}
            />
          )
          : (
            <Editor
              value={value}
              label={field.label}
              {...editorConfig}
              // Valeur secondaire que l'éditeur règle dans la même pop-in
              // (l'intitulé d'un lien) : il la rend s'il la reçoit, et la renvoie
              // en second argument de `onSave` — commitée avec la principale.
              companion={companion}
              onSave={(v, companionValue) => onSave(field.key, v, companionValue)}
              onClose={onClose}
            />
          )}
      </div>

    </div>,
    document.body,
  )
}
